/**
 * Saved Items Vuex module for Portal Application
 * Controls the data and actions for Saved Items lists
 * 
 * @module SavedItems
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date August 2023
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    savedItemsListsPage: [],
    selectedItemList: {},
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getSavedItemsListPages(st) {
        return st.savedItemsListsPage;
    },

    getSelectedList(st) {
        return st.selectedItemList;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },
    
    setSavedItemsListsPage(st, payload) {
        st.savedItemsListsPage = payload;
    },

    setSelectedList(st, payload) {
        st.selectedItemList = payload;
    },
};

const actions = {
    // Get Saved Items Lists
    async fetchSavedItemsLists({ commit }, payload)
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "listName", state: "asc"}),
        };
        
        const response = await portalapi.moduleSavedItems.getSavedItemsLists(requestPayload);

        if (response && response.data) {
            commit("setSavedItemsListsPage", response.data);
        }
    },


    // Get selected list
    async fetchSelectedList({ commit }, listId)
    {
        var response = await portalapi.moduleSavedItems.getSelectedListData(listId);

        // Check if the list was found
        if (!response) {
            response = {
                listFound: false,
            };
        } else {
            response.data.listFound = true;
        }

        commit("setSelectedList", response.data);
    },


    // Update Saved Items List
    async updateListData({ commit }, payload)
    {
        const response = await portalapi.moduleSavedItems.updateListData(payload);

        if (response && response.data) {
            return response.data;
        }

        return false;
    },


    // Create new Saved Items List
    async createNewSavedList({ commit }, payload)
    {
        const response = await portalapi.moduleSavedItems.createSavedItemsList(payload);

        if (response && response.data) {
            return response.data;
        }

        return false;
    },


    // Get Line Item Data
    async getUpdatedLineItemData({ commit }, payload)
    {
        const response = await portalapi.moduleItem.getItemListingData(payload);

        if (response && response.data) {
            return response.data;
        }

        return false;
    },


    // Add list items to cart
    async addListItemsToCart({ commit, dispatch }, payload)
    {
        dispatch("Carts/incrementRegularCartLoadingCount", null, { root: true });
        const response = await portalapi.moduleSavedItems.addListItemsToCart(payload);
        dispatch("Carts/decrementRegularCartLoadingCount", null, { root: true });

        await dispatch("Carts/updateCartData", {
            cartType: vm.config.globalProperties.$constants.CART_TYPE_REGULAR,
            response: response
        }, { root: true });

        if (response && response.data) {
            return response.data;
        }

        return false;
    },


    // Delete a Saved Items List
    async deleteSavedItemsList({ commit }, payload)
    {
        const response = await portalapi.moduleSavedItems.deleteSavedItemsList(payload);

        if (response && response.data) {
            return response.data;
        }

        return false;
    },


    // Get line item data for cart items
    async getCartItemData({ commit }, cartType) 
    {
        const response = await portalapi.moduleSavedItems.getCartItemData(cartType);

        if (response && response.data) {
            return response.data;
        }

        return false;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};